<template>
    <div class="ah-page accounting-account-balance-spreadsheet">
        <div class="page-header">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>會計科目餘額試算表</h1>
                    </div>
                    <div class="actions">
                        <el-button @click="onExport">匯出Excel</el-button>
                    </div>
                </div>
                <div style="height: 80px;"></div>
            </div>
        </div>
        <div class="row" style="margin-top: -85px; margin-bottom: -32px;">
            <div class="ah-container">
                <div class="ah-panel">
                    <div class="panel-body">
                        <search-form>
                            <search-form-item label="科目類型">
                                <el-radio-group v-model="queryParams.QueryType" @change="updateData">
                                    <el-radio-button :label="1">明細科目</el-radio-button>
                                    <el-radio-button :label="2">統制科目</el-radio-button>
                                </el-radio-group>
                            </search-form-item>
                            <search-form-item label="查詢期間">
                                <ahDropdownDatePicker
                                    v-model="dateRange"
                                    :openDate="openDate"
                                    @change="updateData"
                                />
                            </search-form-item>
                            <search-form-item :label="queryParams.QueryType === 1 ? '明細科目起迄' : '統制科目起迄'">
                                <div class="horizontal-inputs">
                                    <ah-select-accounting-setting
                                        v-model="queryParams.AccountStart"
                                        :queryType="queryParams.QueryType"
                                        align="center"
                                        clearable
                                        style="width: 261px"
                                    />
                                    <span class="spacer">至</span>
                                    <ah-select-accounting-setting
                                        v-model="queryParams.AccountEnd"
                                        :queryType="queryParams.QueryType"
                                        align="center"
                                        clearable
                                        style="width: 250px"
                                    />
                                    <el-button type="primary" class="button" @click="updateData" icon="el-icon-search">查詢</el-button>
                                </div>
                            </search-form-item>
                        </search-form>
                    </div>
                </div>
            </div>
        </div>
        <div class="row" style="margin-top: 40px">
            <div class="ah-container">
                <div class="ah-panel">
                    <div class="panel-body" style="padding: 20px 50px 70px 50px">
                        <ah-sticky-el-table-header>
                            <el-table
                                :data="datas"
                                v-loading="isLoading"
                                @row-click="onRowClick"
                                class="accounting-balance-table"
                                :class="{loading: isLoading}"
                                :empty-text="isLoading ? '載入中' : '無資料'"
                            >
                                <el-table-column prop="accno" label="科目代號" width="95"></el-table-column>
                                <el-table-column prop="accnam" label="科目名稱"></el-table-column>
                                <el-table-column prop="dC_TYP" label="屬性" width="60">
                                    <template slot-scope="scope">
                                        <div>
                                            <span v-if="scope.row.dC_TYP === 1" class="accouting-dc-label dr">借</span>
                                            <span v-else class="accouting-dc-label cr">貸</span>
                                        </div>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="exTotalAmount" label="期初借方餘額" width="130" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.dC_TYP === 1 ? scope.row.exTotalAmount : 0" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="exTotalAmount" label="期初貸方餘額" width="130" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.dC_TYP === 2 ? scope.row.exTotalAmount : 0" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="crD_NTD" label="本期借方金額" width="130" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.crD_NTD" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="deB_NTD" label="本期貸方金額" width="130" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.deB_NTD" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="crdTotalAmount" label="期末借方餘額" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.dC_TYP === 1 ? scope.row.exTotalAmount + scope.row.totalAmount : 0" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="debTotalAmount" label="期末貸方餘額" align="right">
                                    <template slot-scope="scope">
                                        <Money :v="scope.row.dC_TYP === 2 ? scope.row.exTotalAmount + scope.row.totalAmount : 0" />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </ah-sticky-el-table-header>
                    </div>
                </div>
            </div>
        </div>
        <DetailDialog
            :visible="detailDialog.visible"
            :StartDate="queryParams.StartDate"
            :EndDate="queryParams.EndDate"
            :accno="detailDialog.accno"
            :accnam="detailDialog.accnam"
            @close="onDetailDialogClose"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import ahSelectAccountingSetting from '@/components/ui/ahSelectAccountingSetting.vue';
import ahStickyElTableHeader from '@/components/ui/ahStickyElTableHeader.vue';
import ahDropdownDatePicker from '@/components/ui/ahDropdownDatePicker.vue';
import SearchForm from '@/components/ui/SearchForm/SearchForm.vue';
import SearchFormItem from '@/components/ui/SearchForm/SearchFormItem.vue';
import { downloadExcel } from './exportExcelUtils';

export default {
    name: 'AccountingAccountBalanceSpreadsheet',
    components: {
        ahDropdownDatePicker,
        ahSelectAccountingSetting,
        ahStickyElTableHeader,
        SearchForm,
        SearchFormItem,
        DetailDialog: () => import('@/components/ui/ahAccountingGeneralLedgerDialog.vue'),
    },
    computed: {
        pickerYearOptions() {
            const START_YEAR = 2020;
            const CURR_YEAR = new Date().getFullYear();
            const shortcuts = [];
            for (let y = CURR_YEAR; y >= START_YEAR; y -= 1) {
                shortcuts.push({
                    text: `${y}`,
                    onClick(picker) {
                        const sd = Number(
                            moment([y])
                                .startOf('year')
                                .format('x'),
                        );
                        const ed = Number(
                            moment([y])
                                .endOf('year')
                                .format('x'),
                        );
                        picker.$emit('pick', [sd, ed]);
                    },
                });
            }
            return { shortcuts };
        },
        isStartEndDateSameYear() {
            return moment(this.dateRange[0]).year() === moment(this.dateRange[1]).year();
        },
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
    },
    data() {
        return {
            isLoading: true,
            dateRange: [null, null],

            queryParams: {
                StartDate: null,
                EndDate: null,
                AccountStart: null,
                AccountEnd: null,
                QueryType: 1,
            },

            datas: [],

            detailDialog: {
                accno: null,
                accnam: null,
                visible: false,
            },
            openDate: null,
        };
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            const today = moment();
            this.dateRange = [
                parseInt(today.startOf('year').format('x'), 10),
                parseInt(today.endOf('year').format('x'), 10),
            ];

            await this.$store.dispatch('accounting/updateAccountingSettings');
            await this.updateData();
        },
        async getYearlyOpenVoucher() {
            const OPEN_DATE = moment(+this.openDate);
            const SELECTED_END_DATE = moment(+this.queryParams.EndDate);
            const SELECTED_START_DATE = moment(+this.queryParams.StartDate);

            if (this.openDate && OPEN_DATE.year() === SELECTED_END_DATE.year() && SELECTED_START_DATE.isAfter(OPEN_DATE)) return;
            if (OPEN_DATE.year() === SELECTED_END_DATE.year() && SELECTED_START_DATE.isBefore(OPEN_DATE)) {
                this.$message({
                    showClose: true,
                    message: `${SELECTED_START_DATE.year()}年開帳日期為 ${OPEN_DATE.format('YYYY-MM-DD')}，已自動將查詢起日設為開帳日期。`,
                    type: 'warning',
                });
            }

            const resp = await this.$api({
                url: 'AccountingSummaryPage/QueryVouchers',
                method: 'GET',
                params: {
                    StartDateStart: moment(+this.queryParams.StartDate)
                        .startOf('year')
                        .format('x'),
                    StartDateEnd: moment(+this.queryParams.StartDate)
                        .endOf('year')
                        .format('x'),
                    TransactionType: 12,
                },
            });

            if (resp.data.totalNum) {
                this.openDate = _.head(resp.data.items)?.dat;
                this.dateRange = [this.openDate, this.dateRange[1]];
            } else this.openDate = null;

            this.updateQuery();
        },
        updateQuery() {
            this.queryParams.StartDate = _.head(this.dateRange);
            this.queryParams.EndDate = _.last(this.dateRange);
        },
        checkQueryValidate() {
            const OPEN_DATE = moment(+this.openDate);
            const SELECTED_DATE = moment(+this.queryParams.EndDate);
            if (this.openDate && SELECTED_DATE.isBefore(OPEN_DATE)) {
                this.$message({
                    showClose: true,
                    message: `${SELECTED_DATE.year()}年開帳日期為 ${OPEN_DATE.format('YYYY-MM-DD')}，請重新選擇日期範圍。`,
                    type: 'error',
                });
                return false;
            }
            return true;
        },
        async updateData() {
            if (!this.isStartEndDateSameYear) {
                this.$message({
                    showClose: true,
                    message: '不可跨年度查詢，請重新選擇日期範圍。',
                    type: 'error',
                });
                return;
            }
            this.updateQuery();
            await this.getYearlyOpenVoucher();
            if (!this.checkQueryValidate()) return;

            this.isLoading = true;
            try {
                const resp = await this.$api({
                    url: '/AccountingSummaryReport/QueryAccountBalance',
                    method: 'GET',
                    params: this.queryParams,
                });

                console.log(resp.data);
                this.datas = _.sortBy(resp.data.items, 'accno');
            } finally {
                this.isLoading = false;
            }
        },
        onRowClick(row) {
            console.log(row);
            this.detailDialog.accno = row.accno;
            this.detailDialog.accnam = row.accnam;
            this.detailDialog.visible = true;
        },
        onDetailDialogClose(refresh) {
            this.detailDialog.visible = false;
            if (refresh) this.updateData();
        },
        async onExport() {
            await downloadExcel(this.queryParams, this.companyName, null);
        },
    },
};
</script>
<style lang="scss">
@import "@/assets/scss/vars.scss";

.accounting-account-balance-spreadsheet {
    &.ah-page {
        padding-bottom: 15px;
    }

    .accounting-balance-table {
        &.loading {
            height: 500px;
        }
    }

    .pagination {
        width: 100%;
        height: 40px;
        display: flex;
        align-items: center;
        text-align: center;
        justify-content: center;
        background-color: #fff;
    }

    .accouting-dc-label {
        &.dr {
            color: #f55c5c;
        }
        &.cr {
            color: #56c500;
        }
    }
}
</style>
